import { useEffect, useState } from "react";
import { Main } from "@/components/Main";
import { getConfigTenants, updateConfigTenant } from "@/Api/configTenant";
import { Settings, Loader2, Save, MapPin, ChevronDown } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { SystemSettings } from "@/App/Settings";

interface ConfigTenant {
  id: number;
  uuid: string;
  key: string;
  value: string;
  description: string;
  data_type?: string;
  config_type?: {
    id: number;
    name: string;
  };
}

export const PageList = () => {
  const [configs, setConfigs] = useState<ConfigTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [editingValues, setEditingValues] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const { data, ok } = await getConfigTenants();
      if (ok && data?.response) {
        setConfigs(data.response);
        const initialValues: Record<string, string> = {};
        data.response.forEach((config: ConfigTenant) => {
          initialValues[config.uuid] = config.value;
        });
        setEditingValues(initialValues);
      }
    } catch (error) {
      console.error("Error fetching configs:", error);
      toast.error("Erro ao carregar configurações");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (config: ConfigTenant) => {
    const newValue = config.value === "true" ? "false" : "true";
    setUpdating(config.uuid);

    try {
      const { ok } = await updateConfigTenant(config.uuid, { value: newValue });
      if (ok) {
        toast.success("Configuração atualizada com sucesso!");
        await fetchConfigs();
      }
    } catch (error) {
      console.error("Error updating config:", error);
      toast.error("Erro ao atualizar configuração");
    } finally {
      setUpdating(null);
    }
  };

  const handleSaveString = async (config: ConfigTenant) => {
    const newValue = editingValues[config.uuid];
    if (newValue === config.value) {
      toast.info("Nenhuma alteração detectada");
      return;
    }

    setUpdating(config.uuid);

    try {
      const { ok } = await updateConfigTenant(config.uuid, { value: newValue });
      if (ok) {
        toast.success("Configuração atualizada com sucesso!");
        await fetchConfigs();
      }
    } catch (error) {
      console.error("Error updating config:", error);
      toast.error("Erro ao atualizar configuração");
    } finally {
      setUpdating(null);
    }
  };

  const pixConfigs = configs.filter(
    (c) => c.key === "pix_sicredi" || c.key === "pix_btg"
  );

  const pixPayoutConfigs = configs
    .filter((c) => c.key === "pix_payout_sicredi" || c.key === "pix_payout_btg")
    .sort((a) => (a.key === "pix_payout_sicredi" ? -1 : 1));

  const mapsConfigs = configs.filter(
    (c) => c.key === "google_maps_api_key"
  );

  const getConfigLabel = (key: string) => {
    switch (key) {
      case "pix_sicredi":
      case "pix_payout_sicredi":
        return "PIX Sicredi";
      case "pix_btg":
      case "pix_payout_btg":
        return "PIX BTG Pactual";
      case "google_maps_api_key":
        return "Chave API Google Maps";
      default:
        return key;
    }
  };

  const getConfigColor = (key: string) => {
    switch (key) {
      case "pix_sicredi":
      case "pix_payout_sicredi":
        return "bg-green-600";
      case "pix_btg":
      case "pix_payout_btg":
        return "bg-blue-600";
      default:
        return "bg-gray-600";
    }
  };

  const renderOperatorCard = (
    title: string,
    description: string,
    list: ConfigTenant[]
  ) => (
            <Collapsible>
              <Card>
                <CollapsibleTrigger className="group w-full text-left">
                  <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
                    <div className="space-y-1.5">
                      <CardTitle className="flex items-center gap-2">
                        {title}
                      </CardTitle>
                      <CardDescription>
                        {description}
                      </CardDescription>
                    </div>
                    <ChevronDown className="mt-1 h-5 w-5 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
              <CardContent>
                <div className="space-y-6">
                  {list.map((config) => {
                    const isActive = config.value === "true";
                    const isUpdating = updating === config.uuid;

                    return (
                      <div
                        key={config.uuid}
                        className="flex items-center justify-between p-4 border rounded-lg"
                      >
                        <div className="flex items-center gap-4">
                          <div
                            className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${getConfigColor(
                              config.key
                            )}`}
                          >
                            {config.key.endsWith("sicredi") ? "S" : "B"}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <Label className="text-base font-semibold">
                                {getConfigLabel(config.key)}
                              </Label>
                              {isActive && (
                                <Badge className="bg-green-500 text-white">
                                  Ativo
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {config.description}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isUpdating ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                          ) : (
                            <Switch
                              checked={isActive}
                              onCheckedChange={() => handleToggle(config)}
                              disabled={isUpdating}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}

                  <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      <strong>Atenção:</strong> Ao ativar uma operadora, a outra será
                      automaticamente desativada. Certifique-se de que a operadora
                      selecionada está configurada corretamente antes de ativá-la.
                    </p>
                  </div>
                </div>
              </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
  );

  return (
    <Main>
      <div className="flex justify-center md:justify-between items-center flex-wrap gap-2 mb-6">
        <h2 className="text-2xl font-semibold flex gap-2 items-center">
          <Settings className="h-6 w-6" />
          Configurações do Sistema
        </h2>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : (
        <div className="grid gap-6">
          {/* Configurações genéricas do sistema (tabelas settings / setting_values) */}
          <SystemSettings />

          {pixConfigs.length > 0 &&
            renderOperatorCard(
              "Operadora de Recebimento PIX - (Vendas)",
              "Escolha qual operadora será utilizada para receber pagamentos via PIX. Apenas uma operadora pode estar ativa por vez.",
              pixConfigs
            )}

          {pixPayoutConfigs.length > 0 &&
            renderOperatorCard(
              "Operadora de Pagamentos / Transferência PIX - (Devoluções)",
              "Escolha qual operadora será utilizada para pagar e transferir via PIX, como as devoluções aos clientes. Apenas uma operadora pode estar ativa por vez.",
              pixPayoutConfigs
            )}

          {mapsConfigs.length > 0 && (
            <Collapsible>
              <Card>
                <CollapsibleTrigger className="group w-full text-left">
                  <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
                    <div className="space-y-1.5">
                      <CardTitle className="flex items-center gap-2">
                        <MapPin className="h-5 w-5" />
                        Mapas - Entregas e Coletas
                      </CardTitle>
                      <CardDescription>
                        Configurações relacionadas à integração com o Google Maps para
                        funcionalidades de entregas e coletas.
                      </CardDescription>
                    </div>
                    <ChevronDown className="mt-1 h-5 w-5 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
              <CardContent>
                <div className="space-y-6">
                  {mapsConfigs.map((config) => {
                    const isUpdating = updating === config.uuid;
                    const hasChanges = editingValues[config.uuid] !== config.value;

                    return (
                      <div key={config.uuid} className="space-y-3">
                        <div>
                          <Label className="text-base font-semibold">
                            {getConfigLabel(config.key)}
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            {config.description}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Input
                            type="text"
                            value={editingValues[config.uuid] || ""}
                            onChange={(e) =>
                              setEditingValues((prev) => ({
                                ...prev,
                                [config.uuid]: e.target.value,
                              }))
                            }
                            placeholder="Digite a chave da API"
                            className="flex-1"
                          />
                          <Button
                            onClick={() => handleSaveString(config)}
                            disabled={isUpdating || !hasChanges}
                            className={cn(colors.bg_primary)}
                          >
                            {isUpdating ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Save className="h-4 w-4 mr-2" />
                                Salvar
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    );
                  })}

                  <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      <strong>Dica:</strong> A chave da API do Google Maps é necessária
                      para utilizar as funcionalidades de mapa, rotas e geolocalização
                      no sistema.
                    </p>
                  </div>
                </div>
              </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          )}
        </div>
      )}
    </Main>
  );
};

export default PageList;
